import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../@/components/ui/card";
import { Trash2, Plus } from "lucide-react";
import { format } from "date-fns";
import { DaySchedule, TimeSlot, BlockedDate } from "../../types/Availability";
import { useToast } from "../../@/components/ui/use-toast";
import { Calendar } from "../../@/components/ui/calendar";
import { Button } from "../../@/components/ui/button";
import { Badge } from "../../@/components/ui/badge";
import {
  checkTimeOverlap,
  formatTimeSlotPayload,
  useDecodedTokenResult,
} from "../../lib/utils";
import DayScheduleRow from "./DayScheduleRow";
import SortupDialog from "../common/SortupDialog";
import moment from "moment";
import {
  useGetVendorProductTimeSlotsMutation,
  useRevokeTokenMutation,
} from "../../api/api";
import { GOOGLE_CALENDAR_AUTH_URL } from "../../lib/constants";
import InfoTooltip from "../common/InfoTooltip";

export default function AvailabilityManager({
  productTimeSlots,
  handleFormatPayload,
  isForProduct,
  isShowSaveButton,
  isPreviewOnly,
  vendorIdFromProp,
  isEditingProduct,
  formState = {},
  modes,
  modeDropdown,
}: {
  productTimeSlots?: any;
  handleFormatPayload?: any;
  isForProduct: boolean;
  isShowSaveButton: boolean;
  isPreviewOnly: boolean;
  vendorIdFromProp?: string | null;
  isEditingProduct: boolean;
  formState?: any;
  setFormState?: React.Dispatch<React.SetStateAction<any>>;
  modes?: any[];
  isDistanceRangApplicable?: boolean;
  setShowAlertOnRouteChange?: React.Dispatch<React.SetStateAction<boolean>>;
  modeDropdown?: React.ReactNode;
}) {
  const [schedule, setSchedule] = useState<DaySchedule[]>([
    { day: "Monday", isAvailable: false, timeSlots: [], hasOverlap: false },
    { day: "Tuesday", isAvailable: false, timeSlots: [], hasOverlap: false },
    { day: "Wednesday", isAvailable: false, timeSlots: [], hasOverlap: false },
    { day: "Thursday", isAvailable: false, timeSlots: [], hasOverlap: false },
    { day: "Friday", isAvailable: false, timeSlots: [], hasOverlap: false },
    { day: "Saturday", isAvailable: false, timeSlots: [], hasOverlap: false },
    { day: "Sunday", isAvailable: false, timeSlots: [], hasOverlap: false },
  ]);

  const [googleCalenderIsAuthorized, setGoogleCalenderIsAuthorized] =
    useState(true);

  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [removedTimeSlots, setRemovedTimeSlots] = useState<number[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { toast } = useToast();
  const lastPayload = useRef<any>(null);
  const { vendorId } = useDecodedTokenResult();
  const [getVendorProductTimeSlots, { isLoading: isGetting }] =
    useGetVendorProductTimeSlotsMutation();

  const [revokeToken, { isLoading: isRevoking }] = useRevokeTokenMutation();

  const toggleDayAvailability = (day: string) => {
    setSchedule((prev) =>
      prev.map((d) =>
        d.day === day
          ? {
              ...d,
              isAvailable: !d.isAvailable,
              timeSlots:
                !d.isAvailable && d.timeSlots.length === 0
                  ? [
                      {
                        start: "09:00 AM",
                        end: "10:00 AM",
                      },
                    ]
                  : d.timeSlots,
            }
          : d
      )
    );
  };

  const addTimeSlot = (day: string, newSlot: TimeSlot) => {
    setSchedule((prev) =>
      prev.map((d) => {
        if (d.day === day) {
          const hasOverlap = checkTimeOverlap(d.timeSlots, newSlot);
          if (hasOverlap) {
            toast({
              title: "Time slot overlaps with existing time slots",
              description: "Please adjust the time slot to avoid conflicts.",
            });
            return d;
          }
          // Add the new slot and sort the time slots
          const updatedTimeSlots = [...d.timeSlots, newSlot].sort((a, b) => {
            const aTime = moment(a.start, "hh:mm A");
            const bTime = moment(b.start, "hh:mm A");
            return aTime.diff(bTime);
          });
          return {
            ...d,
            timeSlots: updatedTimeSlots,
          };
        }
        return d;
      })
    );
  };

  const removeTimeSlot = (day: string, index: number) => {
    const removedSlot = schedule.find((d) => d.day === day)?.timeSlots[index];
    if (removedSlot?.id && isForProduct && removedSlot.productId) {
      setRemovedTimeSlots((prev) => [...prev, removedSlot.id!]);
    } else if (removedSlot?.id && !isForProduct) {
      setRemovedTimeSlots((prev) => [...prev, removedSlot.id!]);
    }
    setSchedule((prev) =>
      prev.map((d) =>
        d.day === day
          ? {
              ...d,
              timeSlots: d.timeSlots.filter((_, i) => i !== index),
            }
          : d
      )
    );
  };

  const applyToAllDays = (sourceDay: string) => {
    const sourceSchedule = schedule.find((d) => d.day === sourceDay);
    if (!sourceSchedule) return;

    const removedTimeSlots: number[] = [];

    setSchedule((prev) =>
      prev.map((d) => {
        if (d.day !== sourceDay && d.isAvailable) {
          // Collect IDs of the existing time slots for removedTimeSlots
          d.timeSlots.forEach((item) => {
            if (item.id) {
              // For product-specific cases, ensure the removed slot belongs to the same product
              if (isForProduct && item.productId) {
                removedTimeSlots.push(item.id);
              } else if (!isForProduct) {
                removedTimeSlots.push(item.id);
              }
            }
          });

          // Filter out slots that conflict (match startTime or endTime)
          // const updatedTimeSlots = sourceSchedule.timeSlots.filter(
          //   (newSlot: any) =>
          //     !d.timeSlots.some(
          //       (existingSlot: any) =>
          //         existingSlot.startTime === newSlot.startTime ||
          //         existingSlot.endTime === newSlot.endTime
          //     )
          // );

          // Ensure no duplication or overwrite of existing slots
          const updatedTimeSlots = [
            ...d.timeSlots.filter(
              (existingSlot: any) =>
                !sourceSchedule.timeSlots.some(
                  (newSlot: any) =>
                    existingSlot.startTime === newSlot.startTime &&
                    existingSlot.endTime === newSlot.endTime
                )
            ),
            ...sourceSchedule.timeSlots.map((newSlot: any) => ({
              ...newSlot,
              id: undefined, // Ensure new slots have no ID
            })),
          ];

          // Return updated schedule with new slots
          return {
            ...d,
            timeSlots: updatedTimeSlots,
            // timeSlots: [
            //   ...updatedTimeSlots.map((item) => ({
            //     ...item,
            //     id: undefined, // Remove IDs from the new slots
            //   })),
            // ],
          };
        }
        return d;
      })
    );
    setRemovedTimeSlots((prev) => [...prev, ...removedTimeSlots]);

    toast({
      title: "Time slots applied to all available days",
      description: `Time slots from ${sourceDay} have been applied to all available days.`,
    });
  };

  const addBlockedDate = (date: Date) => {
    const formattedDate = format(date, "yyyy-MM-dd");

    if (!blockedDates.some((blocked) => blocked.date === formattedDate)) {
      const newBlockedDate = { date: formattedDate, id: undefined };
      setBlockedDates((prev) =>
        [...prev, newBlockedDate].sort((a, b) => a.date.localeCompare(b.date))
      );
    }
    setIsCalendarOpen(false);
  };

  const removeBlockedDate = (date: string) => {
    const removeBlockedDate = blockedDates.find((d) => d.date === date);
    if (removeBlockedDate?.id && isForProduct && removeBlockedDate.productId) {
      setRemovedTimeSlots((prev) => [...prev, removeBlockedDate.id!]);
    } else if (removeBlockedDate?.id && !isForProduct) {
      setRemovedTimeSlots((prev) => [...prev, removeBlockedDate.id!]);
    }
    setBlockedDates((prev) => prev.filter((d) => d.date !== date));
  };

  const handleRevokeToken = async () => {
    try {
      const response = await revokeToken({
        body: {
          vendorId: vendorId,
        },
      }).unwrap();
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Token revoked successfully",
        });
        setGoogleCalenderIsAuthorized(false);
      }
    } catch (error) {
      console.error("Error revoking token:", error);
      toast({
        title: "Error",
        description: "Failed to revoke token",
      });
    }
  };

  const fetchVendorProductTimeSlots = async () => {
    try {
      let vendorIdFormat = parseInt(vendorIdFromProp || "0");
      const response = await getVendorProductTimeSlots({
        body: {
          vendorId: vendorIdFormat !== 0 ? vendorIdFormat : vendorId,
        },
      }).unwrap();

      setGoogleCalenderIsAuthorized(response.data.isVenodrAuthorized);

      if (response.data.vendorProductTimeSlots.length > 0) {
        // Group time slots by dayOfWeek
        const groupedByDay = response.data.vendorProductTimeSlots.reduce(
          (acc: Record<string, any[]>, item) => {
            if (item.dayOfWeek) {
              const parsedStartTime = new Date(item.startTime);
              const getDayOfWeek = moment(parsedStartTime).format("dddd");
              acc[getDayOfWeek] = acc[getDayOfWeek] || [];
              acc[getDayOfWeek].push(item);
            } else {
              // If no dayOfWeek, treat it as a blocked date
              const formattedDate = moment(item.startTime).format("YYYY-MM-DD");
              setBlockedDates((prev) => {
                if (!prev.some((blocked) => blocked.date === formattedDate)) {
                  return [
                    ...prev,
                    {
                      date: formattedDate,
                      id: item.id,
                      productId: item.productId,
                    },
                  ].sort((a, b) => a.date.localeCompare(b.date));
                }
                return prev;
              });
            }
            return acc;
          },
          {}
        );

        const updatedSchedule = schedule.map((day) => {
          const dayData = groupedByDay[day.day];
          if (dayData) {
            return {
              ...day,
              isAvailable: true,
              timeSlots: dayData.map((slot) => ({
                start: moment(slot.startTime).format("hh:mm A"),
                end: moment(slot.endTime).format("hh:mm A"),
                id: slot.id,
                productId: slot.productId,
              })),
            };
          }
          return {
            ...day,
            isAvailable: false,
            timeSlots: [],
          };
        });

        setSchedule(updatedSchedule);
      }
    } catch (error) {
      console.error("Error fetching vendor product time slots:", error);
    }
  };

  useEffect(() => {
    if (isForProduct || handleFormatPayload) {
      const {
        adjustedBlockedDates,
        timeSlotsPayload,
        updatedUnavailableSlotsIds,
      } = formatTimeSlotPayload(
        schedule,
        blockedDates,
        isForProduct,
        isEditingProduct
      );

      const payload = {
        timeSlotsPayload,
        adjustedBlockedDates,
        removedTimeSlots,
        updatedUnavailableSlotsIds,
      };

      if (JSON.stringify(payload) !== JSON.stringify(lastPayload.current)) {
        lastPayload.current = payload;
        handleFormatPayload(
          timeSlotsPayload,
          adjustedBlockedDates,
          removedTimeSlots,
          updatedUnavailableSlotsIds
        );
      }
    }
  }, [
    handleFormatPayload,
    schedule,
    blockedDates,
    removedTimeSlots,
    isForProduct,
  ]);

  const handleAuthorize = () => {
    window.open(GOOGLE_CALENDAR_AUTH_URL, "_self");
  };

  useEffect(() => {
    if (!isForProduct) {
      fetchVendorProductTimeSlots();
    } else if (isForProduct && productTimeSlots.length > 0) {
      if (productTimeSlots.length > 0) {
        // Group time slots by dayOfWeek
        const groupedByDay = productTimeSlots.reduce(
          (acc: Record<string, any[]>, item: any) => {
            if (item.dayOfWeek) {
              const parsedStartTime = new Date(item.startTime);
              const getDayOfWeek = moment(parsedStartTime).format("dddd");
              acc[getDayOfWeek] = acc[getDayOfWeek] || [];
              acc[getDayOfWeek].push(item);
            } else {
              // If no dayOfWeek, treat it as a blocked date
              const formattedDate = moment(item.startTime).format("YYYY-MM-DD");
              setBlockedDates((prev) => {
                if (!prev.some((blocked) => blocked.date === formattedDate)) {
                  return [
                    ...prev,
                    {
                      date: formattedDate,
                      id: item.id,
                      productId: item.productId,
                    },
                  ].sort((a, b) => a.date.localeCompare(b.date));
                }
                return prev;
              });
            }
            return acc;
          },
          {}
        );

        const updatedSchedule = schedule.map((day) => {
          const dayData = groupedByDay[day.day];
          if (dayData) {
            return {
              ...day,
              isAvailable: true,
              timeSlots: dayData.map((slot: any) => ({
                start: moment(slot.startTime).format("hh:mm A"),
                end: moment(slot.endTime).format("hh:mm A"),
                id: slot.id,
                productId: slot.productId,
              })),
            };
          }
          return {
            ...day,
            isAvailable: false,
            timeSlots: [],
          };
        });
        setSchedule(updatedSchedule);
      }
    } else if (isForProduct && !isEditingProduct) {
      fetchVendorProductTimeSlots();
    }
  }, [vendorId, productTimeSlots, isForProduct]);

  if (!formState) {
    // Handle the case where formState is not provided
    return null; // or some fallback UI
  }

  return (
    <div className="w-full">
      {isShowSaveButton && (
        <div className="flex justify-end mb-2 gap-2">
          {!googleCalenderIsAuthorized && (
            <Button
              type="button"
              onClick={handleAuthorize}
              className="w-full sm:w-auto bg-customGreenColor hover:bg-customGreenDark"
            >
              Authorize with Google Calendar
            </Button>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="h-fit px-6">
          <CardTitle className="text-xl font-bold flex items-center mt-6">
            Availability Schedule
            <InfoTooltip
              content="Set your availability schedule for each day of the week. You can add multiple time slots for each day."
              className="ml-2 font-normal"
            />
          </CardTitle>
          <CardContent className="space-y-6 my-6">
            {isGetting
              ? Array.from({ length: 7 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="h-12 bg-gray-200 rounded-md mb-2 p-2"></div>
                  </div>
                ))
              : schedule.map((day) => (
                  <DayScheduleRow
                    isPreviewOnly={isPreviewOnly}
                    key={day.day}
                    day={day}
                    onToggleAvailability={() => toggleDayAvailability(day.day)}
                    onAddTimeSlot={addTimeSlot}
                    onRemoveTimeSlot={removeTimeSlot}
                    onApplyToAll={() => applyToAllDays(day.day)}
                    showApplyToAll={
                      day.day === "Monday" && day.timeSlots.length > 0
                    }
                  />
                ))}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          <Card className="h-fit px-6">
            <CardHeader className="flex flex-col md:flex-row items-center justify-between py-3">
              <CardTitle className="w-full md:w-auto text-xl font-bold flex items-center">
                Blocked Dates
                <div style={{ marginTop: "2px" }}>
                  <InfoTooltip
                    content="Block dates when you are unavailable to provide services."
                    className="ml-2 font-medium"
                  />
                </div>
              </CardTitle>
              {!isPreviewOnly && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full md:w-auto"
                  onClick={() => setIsCalendarOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Blocked Dates
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 pb-6">
                {isGetting
                  ? Array.from({ length: 8 }).map((_, index) => (
                      <div
                        key={index}
                        className="h-10 w-32 bg-gray-200 animate-pulse rounded-md flex items-center justify-center"
                      />
                    ))
                  : blockedDates.map((date: BlockedDate) => (
                      <Badge
                        key={date.date}
                        variant="secondary"
                        className="flex items-center gap-2 h-10 rounded-md"
                      >
                        {format(new Date(date.date), "MMM dd, yyyy")}
                        <button
                          type="button"
                          disabled={isPreviewOnly}
                          onClick={() => removeBlockedDate(date.date)}
                          className="text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                {!isGetting && blockedDates.length === 0 && (
                  <p className="text-muted-foreground pb-3">No blocked dates</p>
                )}
              </div>
            </CardContent>
          </Card>

          {isForProduct && modeDropdown && (
            <Card className="h-fit px-6 pb-6">
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <CardTitle className="text-xl font-bold">Mode</CardTitle>
              </CardHeader>
              <CardContent className="mb-1 flex items-center">
                <label className="block font-medium text-sm text-customGrayDark items-center">
                  Select a mode<span className="text-red-500">*</span>
                </label>
                <InfoTooltip
                  content="Select service type: Virtual (online), Walk-in (in-store), or Home (at your location)"
                  className="ml-2"
                />
              </CardContent>
              <CardContent>{modeDropdown}</CardContent>
            </Card>
          )}
        </div>
        <SortupDialog
          header="Select Date"
          isOpen={isCalendarOpen}
          bodyClassName="w-full flex justify-center items-center p-4"
          onClose={() => setIsCalendarOpen(false)}
        >
          <div className="flex justify-center items-center w-full">
            <Calendar
              mode="single"
              onSelect={(date) => date && addBlockedDate(date)}
              disabled={(date) => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                return (
                  date < yesterday ||
                  blockedDates.some(
                    (blocked) => blocked.date === format(date, "yyyy-MM-dd")
                  )
                );
              }}
              className="rounded-md border"
            />
          </div>
        </SortupDialog>
      </div>
    </div>
  );
}
